import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SearchTypeEnum, SortFieldEnum, SortOrderEnum } from '@seeqdev/api/model/PropertySearchV1';
import { SeeqNames } from '@seeqdev/constants';
import type { ItemFinderSearchesInputV1 } from '@seeqdev/api/model/ItemFinderSearchesInputV1';
import type { ItemFinderSearchesOutputV2 } from '@seeqdev/api/model/ItemFinderSearchesOutputV2';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { PropertyFilterInputV1 } from '@seeqdev/api/model/PropertyFilterInputV1';
import type { PropertySearchV1 } from '@seeqdev/api/model/PropertySearchV1';

type RequestOverrides = Parameters<typeof sqItemsApi.executeItemFinderSearchesV2>[1];

/**
 * Builders for ad-hoc ItemFinder searches (`POST /items/finders/v2/search`), the replacement for the deprecated
 * `GET/POST /items` search. Call sites describe what they want as typed predicates; these helpers phrase it the way
 * ItemFinder needs: every search names concrete types, carries a sort so it can be resumed with a continuation
 * token, and expresses "scoped to this workbook or global" as a WITHIN over the workbook ids and null.
 *
 * The regex helpers are a port of `Regexes.kt` so the patterns sent to Postgres are identical to the ones the legacy
 * endpoint built server-side from the same user input; SPy ports the same conversion for its own migration
 * (CRAB-64893).
 */

export type PredicateValue = string | number | boolean | null;

export function predicate(
  propertyName: string,
  operator: OperatorEnum,
  ...values: PredicateValue[]
): PropertyFilterInputV1 {
  return { propertyName, operator, values: values.map((value) => ({ value })) };
}

export function withinPredicate(propertyName: string, values: PredicateValue[]): PropertyFilterInputV1 {
  return predicate(propertyName, OperatorEnum.WITHIN, ...values);
}

/**
 * Items scoped to any of the workbooks, plus globally scoped items unless excluded. A null value matches items
 * with no Scoped To property.
 */
export function scopedToPredicate(
  workbookIds: string[],
  { excludeGloballyScoped = false }: { excludeGloballyScoped?: boolean } = {},
): PropertyFilterInputV1 {
  return withinPredicate(SeeqNames.Properties.ScopedTo, excludeGloballyScoped ? workbookIds : [null, ...workbookIds]);
}

/**
 * The server injects `Unsearchable NOT_EQUALS true` into any property search that does not mention Unsearchable.
 * Naming the property with every value it can take suppresses that default without filtering anything out. Null is
 * one of those values: the formula_items table keeps extra properties in JSONB and never writes Unsearchable, so a
 * FormulaItem's Unsearchable reads as null there and a list without null matches no FormulaItem at all.
 */
export function includeUnsearchablePredicate(): PropertyFilterInputV1 {
  return withinPredicate(SeeqNames.Properties.Unsearchable, [true, false, null]);
}

// "*" and "?" are deliberately not escaped; the globbing logic below turns them into ".*" and ".". "#" must be
// escaped because the server's regex engine otherwise treats it as the start of a comment.
const REGEX_SPECIAL_CHARS = /([.+\-[\](){}<>^|$#@&~\\"])/g;
const REGEX_IS_REGEX = /^\/[\s\S]*\/[ism]*$/;

export function isPostgresRegexDelimited(search: string): boolean {
  return REGEX_IS_REGEX.test(search);
}

function extractPostgresRegexBody(search: string, asSubstring: boolean): string {
  const endIndex = search.lastIndexOf('/');
  let regex = search.substring(1, endIndex);
  if (!asSubstring) {
    if (!regex.startsWith('^')) {
      regex = `^${regex}`;
    }
    if (!regex.endsWith('$')) {
      regex = `${regex}$`;
    }
  }

  // Users write /.../i for case-insensitivity; Postgres wants the (?i) prefix instead
  const modifiers = search.substring(endIndex + 1);
  return modifiers ? `(?${modifiers})${regex}` : regex;
}

/**
 * Turns a Seeq search string into a Postgres regex. Search strings are either globs ("*" for any run of characters,
 * "?" for one) or regexes delimited by "/". A glob is wrapped in "^…$" unless asSubstring; a delimited regex is
 * anchored the same way but its case sensitivity is left to its own modifiers.
 */
export function convertInputStringToPostgresRegex(search: string, asSubstring: boolean, caseInsensitive: boolean) {
  if (!search) {
    return asSubstring ? '.*' : '';
  }

  if (isPostgresRegexDelimited(search)) {
    return extractPostgresRegexBody(search, asSubstring);
  }

  let regex = search.replace(REGEX_SPECIAL_CHARS, '\\$1').replaceAll('*', '.*').replaceAll('?', '.');
  if (!asSubstring) {
    regex = `^${regex}$`;
  }

  // All globs should match newline characters
  return `(?s${caseInsensitive ? 'i' : ''})${regex}`;
}

function hasGlob(text: string): boolean {
  return text.includes('*') || text.includes('?');
}

/**
 * STRING_CONTAINS becomes an ILIKE pattern on the server, which escapes `%` and `_` but not the backslash that
 * Postgres treats as the escape character, so a backslash can only be matched literally through a regex.
 */
const needsRegexForContains = (text: string) => text.includes('\\');

/**
 * Matches user-entered text against a property the way the legacy search box did: surrounding whitespace ignored,
 * blank text matching everything, case-insensitive, and either a substring match (the default) or a whole-value
 * match. Plain text uses the indexed string operators; globs, delimited regexes and substrings containing a backslash
 * become REGEX_MATCHES.
 */
export function userTextPredicate(
  propertyName: string,
  rawText: string,
  { exact = false }: { exact?: boolean } = {},
): PropertyFilterInputV1 {
  const text = rawText.trim();
  if (text === '') {
    // The legacy endpoint ignored a blank clause, so blank text constrains nothing in either mode
    return predicate(propertyName, OperatorEnum.STRINGCONTAINS, '');
  }

  const isPattern = hasGlob(text) || isPostgresRegexDelimited(text) || (!exact && needsRegexForContains(text));
  if (!isPattern) {
    return predicate(propertyName, exact ? OperatorEnum.EQUALSIGNORECASE : OperatorEnum.STRINGCONTAINS, text);
  }

  if (exact) {
    const regex = convertInputStringToPostgresRegex(text, false, true);
    // An exact match is case-insensitive like EQUALS_IGNORE_CASE unless a delimited regex sets its own modifiers
    return predicate(propertyName, OperatorEnum.REGEXMATCHES, regex.startsWith('(?') ? regex : `(?i)${regex}`);
  }

  // A glob with "*" is anchored because the user has placed the wildcards themselves
  return predicate(
    propertyName,
    OperatorEnum.REGEXMATCHES,
    convertInputStringToPostgresRegex(text, !text.includes('*'), true),
  );
}

export interface PropertySearchOptions {
  types: string[];
  predicates?: PropertyFilterInputV1[];
  ancestorId?: string;
  sortField?: SortFieldEnum;
  sortOrder?: SortOrderEnum;
}

/** The operators that exclude items; the server does not count them as narrowing a search */
const EXCLUSION_OPERATORS = new Set<OperatorEnum>([
  OperatorEnum.NOTEQUALS,
  OperatorEnum.NOTSTRINGCONTAINS,
  OperatorEnum.NOTREGEXMATCHES,
]);

/**
 * An include search over the given types. A sort is always set because a search without one cannot be resumed
 * from a continuation token, and a search nothing narrows matches every item of the types.
 */
export function propertySearch({
  types,
  predicates = [],
  ancestorId,
  sortField = SortFieldEnum.ID,
  sortOrder = SortOrderEnum.ASC,
}: PropertySearchOptions): PropertySearchV1 {
  if (types.length === 0) {
    throw new Error('An ItemFinder property search requires at least one item type');
  }

  // The server rejects a search that neither an ancestor nor a non-exclusion predicate narrows, so match every item
  const isNarrowed = ancestorId !== undefined || predicates.some(({ operator }) => !EXCLUSION_OPERATORS.has(operator));
  return {
    searchType: SearchTypeEnum.PROPERTY,
    isInclude: true,
    types,
    predicates: isNarrowed
      ? predicates
      : [...predicates, predicate(SeeqNames.Properties.Name, OperatorEnum.STRINGCONTAINS, '')],
    ...(ancestorId === undefined ? {} : { ancestorId }),
    sortField,
    sortOrder,
  };
}

export async function searchItemFinder(
  body: ItemFinderSearchesInputV1,
  requestOverrides?: RequestOverrides,
): Promise<ItemFinderSearchesOutputV2> {
  const { data } = await sqItemsApi.executeItemFinderSearchesV2(body, requestOverrides);
  return data;
}

export interface FindItemsByIdsOptions {
  /** The item types the ids may belong to; the server requires at least one */
  types: string[];
  includeProperties?: string[];
  /** Also return archived items, which a search otherwise leaves out */
  includeArchived?: boolean;
}

/**
 * Looks items up by id. An id that is missing or unreadable is simply absent from the result, where a fixed-list
 * search would reject the whole request. Unsearchable items are always included.
 */
export async function findItemsByIds(
  itemIds: string[],
  { types, includeProperties, includeArchived = false }: FindItemsByIdsOptions,
  requestOverrides?: RequestOverrides,
): Promise<ItemSearchPreviewV1[]> {
  if (itemIds.length === 0) {
    return [];
  }

  const predicates = [withinPredicate(SeeqNames.Properties.Id, itemIds), includeUnsearchablePredicate()];
  // The server only lifts its archived filter for an explicit EQUALS (CRAB-66069), so each value is its own search
  const propertySearches = includeArchived
    ? [false, true].map((archived) =>
        propertySearch({
          types,
          predicates: [...predicates, predicate(SeeqNames.Properties.Archived, OperatorEnum.EQUALS, archived)],
        }),
      )
    : [propertySearch({ types, predicates })];

  const { items } = await searchItemFinder(
    { propertySearches, limit: itemIds.length, includeProperties },
    requestOverrides,
  );
  return items;
}
